import React, { useContext, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Grid, Tooltip, Button } from '@material-ui/core';
import { Container, Panel } from '../../../../Components';
import { CompanyUnits } from './CompanyUnits/CompanyUnits.List';
import { CompanyUnitEditForm } from './CompanyUnits/CompanyUnits.Edit';
import { EditProcess } from './CompanyUnits/Processes';
import { EditAspect } from './CompanyUnits/Aspects';
import { EditTag } from './CompanyUnits/Tags';
import { CompanyUsers } from './Users/Users.List';
import { UserEditForm } from './Users/Users.Edit';
import { UserInviteForm } from './Users/Users.Create';
import { CustomerLogotypeForm } from './LogotypeForm/CustomerLogotypeForm';
import { AzureADSettings } from './AzureAD';
import { ModuleContext } from '../../../../Context/ModuleContext/ModuleContext';
import { AccessLevel, OrganizationFeature, UserContext } from '../../../../Context/UserContext/UserContext';
import { ArrowUpward } from '@material-ui/icons';
import { useStyles } from "./Organization.styles";

export const Organization = (props: { suppressHeader?: boolean }) => {
  const { formatMessage } = useIntl();
  const { setHeader } = useContext(ModuleContext);
  const { hasAccessToFeature } = useContext(UserContext);
  const canManage = hasAccessToFeature(OrganizationFeature.Manage, AccessLevel.Manage);
  const canManageAAD = hasAccessToFeature(OrganizationFeature.AAD, AccessLevel.Manage);
  const classes = useStyles();
  const canAdminister = hasAccessToFeature(OrganizationFeature.Manage, AccessLevel.Administer);

  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    if (!props.suppressHeader) {
      setHeader(formatMessage({ id: 'organization.header', defaultMessage: 'Organization' }));
    }
  }, [setHeader, formatMessage, props.suppressHeader]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const mainContent = (
    <Grid container spacing={4} direction="column" justifyContent="space-evenly" wrap="nowrap">
      <Grid item>
        <Panel titleKey="organization.company-division" accordion>
          <CompanyUnits />
        </Panel>
      </Grid>
      {canAdminister && (
        <Grid item>
          <Panel titleKey="organization.users" accordion>
            <CompanyUsers />
          </Panel>
        </Grid>
      )}
      {canAdminister && (
        <Grid item>
          <Panel titleKey="organization.logotype" accordion>
            <CustomerLogotypeForm />
          </Panel>
        </Grid>
      )}
      {canManageAAD && (
        <Grid item>
          <Panel titleKey="organization.azure-ad" accordion>
            <AzureADSettings />
          </Panel>
        </Grid>
      )}
      {showScrollTop && (
        <Tooltip title={formatMessage({ id: "bottom.to.top.btn.tooltip.text", defaultMessage: "Back to top" })}>
          <Button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={classes.topButton}
          >
            <ArrowUpward style={{ fontSize: 20 }} />
          </Button>
        </Tooltip>
      )}
    </Grid>
  );

  return (
    <Container>
      <Routes>
        <Route path="/" element={mainContent} />
        {canManage && (
          <Route path="company-units/:id" element={<CompanyUnitEditForm />} />
        )}
        {canManage && (
          <Route path="company-units/:companyUnitId/users/new" element={<UserInviteForm />} />
        )}
        {canManage && (
          <Route path="company-units/:companyUnitId/users/:id" element={<UserEditForm />} />
        )}
        {canAdminister && (
          <Route path="users/new" element={<UserInviteForm />} />
        )}
        {canAdminister && (
          <Route path="users/:id" element={<UserEditForm />} />
        )}
        {canManage && (
          <Route path="company-units/:unitId/processes/:id" element={<EditProcess />} />
        )}
        {canManage && (
          <Route path="company-units/:unitId/aspects/:id" element={<EditAspect />} />
        )}
        {canManage && (
          <Route path="company-units/:unitId/tags/:id" element={<EditTag />} />
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Container>
  );
};